import React from 'react';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
  Heading,
  Container,
  Type,
} from '@nbcuniversal-paint/nbcu-react-components';
import ComponentLoader from '../../components/ComponentLoader';
import selectScheduleItems from '../../modules/schedule/schedule.selectors';
import styles from './ScheduleList.module.scss';
import strings from '../../translations';
import List from '../../components/List';
import { formatDate, getIsoDate } from '../../tools/dateFuncs';
import ScheduleNav from '../../components/ScheduleNav';
import { AWS_ENDPOINT, EPG_REGION } from '../../constants';
import fetchScheduleData from '../../modules/schedule/schedule.actions';
import { State, Entity } from '../../modules/schedule/schedule';
import LogoMono from '../../components/Elements/LogoMono';

const { TITLE, NO_CONTENT, DOWNLOAD_BTN_TEXT } = strings.SCHEDULE;

type Props = {
  className?: string;
  error?: boolean;
  currentDate: string;
};

import axios from 'axios';

const ScheduleList = ({ className, error, currentDate }: Props) => {
  const [loading, setLoading] = React.useState(true);
  const [activeDate, setActiveDate] = React.useState<string>(
    String(currentDate),
  );
  const [activeURL, setActiveURL] = React.useState(
    `${AWS_ENDPOINT}/${EPG_REGION}/${currentDate}.json`,
  );
  const dispatch = useDispatch();
  const scheduleItems = useSelector((state: State) =>
    selectScheduleItems(state, currentDate),
  );

  const epgName = import.meta.env.VITE_EPGNAME;

  React.useEffect(() => {
    /**
     * Fetch data.
     */
    async function fetchData() {
      setLoading(true);
      await dispatch(fetchScheduleData(String(currentDate)));
      setLoading(false);
    }

    fetchData();
  }, [currentDate, dispatch]);

  const handleDownload = async () => {
    try {
      const response = await axios.get(activeURL, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `schedule_${activeDate}.json`;
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const updateActiveDate = async (
    event: React.MouseEvent<HTMLButtonElement> | null,
    manualDate = null,
  ) => {
    const newDate =
      manualDate || event?.currentTarget?.dataset?.date || activeDate;
    setLoading(true);
    await dispatch(fetchScheduleData(String(newDate)));
    setLoading(false);
    setActiveDate(newDate);
    setActiveURL(`${AWS_ENDPOINT}/${EPG_REGION}/${newDate}.json`);
  };

  /**
   * Sort the schedule items by date of each item.
   *
   * @param {Array} data
   */
  const sortSchedule = (data: Entity[]): Entity[] => {
    const formattedDate = getIsoDate(currentDate);
    const todaysDate = formatDate(formattedDate);
    return data.sort((a: Entity, b: Entity) => {
      const ad = new Date(`${todaysDate} ${a.time}`);
      const bd = new Date(`${todaysDate} ${b.time}`);
      return Number(ad) - Number(bd);
    });
  };

  if (error)
    return (
      <>
        <div className={cx(className, styles.layout)}>
          <div className={styles.wrapper}>
            <Heading tag="h1" className={cx('h1', styles.heading)}>
              {TITLE}
            </Heading>
            <ScheduleNav
              currentDate={currentDate}
              callBack={updateActiveDate}
            />
            <Container>
              <Type as="p">{NO_CONTENT}</Type>
            </Container>
          </div>
        </div>
      </>
    );

  if (!scheduleItems) return null;

  const sortedSchedule = sortSchedule(scheduleItems);
  return (
    <>
      <div className={cx(className, styles.layout)}>
        <div className={cx('schedule-list-wrapper', styles.wrapper)}>
          {epgName !== '13emerue' && epgName !== 'syfy' && (
            <Heading tag="h1" className={cx('h1', styles.heading)}>
              {TITLE}
            </Heading>
          )}

          {(epgName === '13emerue' || epgName === 'syfy') && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <LogoMono width="120" />
              <p
                style={{
                  paddingLeft: '20px',
                  color: '#fff',
                  fontFamily:
                    epgName === 'syfy'
                      ? 'Oxanium'
                      : 'NudistaScar-Bold, sans-serif',
                  fontSize: epgName === 'syfy' ? '72px' : '2.625rem',
                }}
              >
                {strings.SCHEDULE.TITLE}
              </p>
            </div>
          )}

          <ScheduleNav currentDate={currentDate} callBack={updateActiveDate} />
          {loading && (
            <div className={styles.loader}>
              <ComponentLoader />
            </div>
          )}
          {!loading && (
            <Container>
              <List items={sortedSchedule} />
            </Container>
          )}
        </div>
        <div>
          <button
            className={styles.downloadBtn}
            style={{ cursor: 'pointer', background: 'gray', color: 'white' }}
            onClick={handleDownload}
          >
            {DOWNLOAD_BTN_TEXT}
          </button>
        </div>
      </div>
    </>
  );
};

export default React.memo(ScheduleList);
